import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styled from "@emotion/styled";
import { Button, Flex, InputCity, InputStreet, InputText, RadioGroup, Validation } from "src/components";
import { i18n, openAddressModal, useAppDispatch, useForm } from "src/common";
import { FieldConfig, Form, FormConfig, FormRef } from "./form";

export type DeliveryFormItemType = 'asEntity' | 'name' | 'city' | 'street' | 'houseNumber';
export const allDeliveryFormItems: Array<DeliveryFormItemType> = ['asEntity', 'name', 'city', 'street', 'houseNumber'];

const FlexStyle = styled(Flex)({
  width: '100%',
  marginTop: 24
})

interface DeliveryFormProps {
  delivery: any;
  formItems?: Array<DeliveryFormItemType>;
  hideContractorOption?: boolean;
  addressData?: { insuredPersonAddress?: any, contractorAddress?: any, validation?: boolean };
  storedValues?: any;
}

export const DeliveryForm = forwardRef<FormRef, DeliveryFormProps>(({ delivery, formItems = allDeliveryFormItems, hideContractorOption = false, addressData, storedValues }, ref) => {
  const { formRef, submitForm, getValues, setFieldValue, validateForm, scrollIntoView } = useForm();
  const [deliveryInitialValues, setDeliveryInitialValues] = React.useState<any | undefined>(undefined);
  const dispatch = useAppDispatch();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      submitForm();
    },
    getValues: () => getValues(),
    setFieldValue: (fieldName: string, value: any) => setFieldValue(fieldName, value),
    validateForm: (validateFields?: string[]) => validateForm(validateFields),
    scrollIntoView: () => scrollIntoView()
  }));

  const [asEntity, setAsEntity] = useState<string>(storedValues?.asEntity || delivery.asEntity.value || 'insured');

  const [allAddressData, setAllAddressData] = useState<{ insuredPersonAddress?: any, contractorAddress?: any }>({ insuredPersonAddress: {}, contractorAddress: {} });

  useEffect(() => {
    if (asEntity === 'insured' && addressData?.insuredPersonAddress) {
      const { insuredPersonAddress } = addressData;
      if (insuredPersonAddress.name || insuredPersonAddress.name === '') setFieldValue('name', insuredPersonAddress.name, addressData.validation);
      if (insuredPersonAddress.city || insuredPersonAddress.city === '') setFieldValue('city', insuredPersonAddress.city, addressData.validation);
      if (insuredPersonAddress.cityKompareId || insuredPersonAddress.cityKompareId === '') setFieldValue('cityKompareId', insuredPersonAddress.cityKompareId, addressData.validation);
      if (insuredPersonAddress.street || insuredPersonAddress.street === '') setFieldValue('street', insuredPersonAddress.street, addressData.validation);
      if (insuredPersonAddress.streetKompareId || insuredPersonAddress.streetKompareId === '') setFieldValue('streetKompareId', insuredPersonAddress.streetKompareId, addressData.validation);
      if (insuredPersonAddress.houseNumber || insuredPersonAddress.houseNumber === '') setFieldValue('houseNumber', insuredPersonAddress.houseNumber, addressData.validation);
      if (insuredPersonAddress.zipCode || insuredPersonAddress.zipCode === '') setFieldValue('zipCode', insuredPersonAddress.zipCode, addressData.validation);
    }
    if (asEntity === 'contractor' && addressData?.contractorAddress) {
      const { contractorAddress } = addressData;
      if (contractorAddress.name || contractorAddress.name === '') setFieldValue('name', contractorAddress.name, addressData.validation);
      if (contractorAddress.city || contractorAddress.city === '') setFieldValue('city', contractorAddress.city, addressData.validation);
      if (contractorAddress.cityKompareId || contractorAddress.cityKompareId === '') setFieldValue('cityKompareId', contractorAddress.cityKompareId, addressData.validation);
      if (contractorAddress.street || contractorAddress.street === '') setFieldValue('street', contractorAddress.street, addressData.validation);
      if (contractorAddress.streetKompareId || contractorAddress.streetKompareId === '') setFieldValue('streetKompareId', contractorAddress.streetKompareId, addressData.validation);
      if (contractorAddress.houseNumber || contractorAddress.houseNumber === '') setFieldValue('houseNumber', contractorAddress.houseNumber, addressData.validation);
      if (contractorAddress.zipCode || contractorAddress.zipCode === '') setFieldValue('zipCode', contractorAddress.zipCode, addressData.validation);
    }
 
    if (addressData) setAllAddressData(addressData)
  }, [addressData]);

  const handleAsEntityChange = (value: string) => {

    if (value === 'insured') {
      setFieldValue('name', allAddressData?.insuredPersonAddress?.name, true);
      setFieldValue('city', allAddressData?.insuredPersonAddress?.city, true);
      setFieldValue('cityKompareId', allAddressData?.insuredPersonAddress?.cityKompareId, true);
      setFieldValue('street', allAddressData?.insuredPersonAddress?.street, true);
      setFieldValue('streetKompareId', allAddressData?.insuredPersonAddress?.streetKompareId, true);
      setFieldValue('houseNumber', allAddressData?.insuredPersonAddress?.houseNumber, true);
      setFieldValue('zipCode', allAddressData?.insuredPersonAddress?.zipCode, true);
    }
    if (value === 'contractor') {
      setFieldValue('name', allAddressData?.contractorAddress?.name, true);
      setFieldValue('city', allAddressData?.contractorAddress?.city, true);
      setFieldValue('cityKompareId', allAddressData?.contractorAddress?.cityKompareId, true);
      setFieldValue('street', allAddressData?.contractorAddress?.street, true);
      setFieldValue('streetKompareId', allAddressData?.contractorAddress?.streetKompareId, true);
      setFieldValue('houseNumber', allAddressData?.contractorAddress?.houseNumber, true);
      setFieldValue('zipCode', allAddressData?.contractorAddress?.zipCode, true);
    }
    if (value === 'third') {
      setFieldValue('name', '');
      setFieldValue('city', '');
      setFieldValue('cityKompareId', '');
      setFieldValue('street', '');
      setFieldValue('streetKompareId', '');
      setFieldValue('houseNumber', '');
      setFieldValue('zipCode', null);
    }

    setAsEntity(value);
  }

  const handleSubmit = (values: { [key: string]: any }) => {
    console.log('Submitted values:', values);
  };

  const radioOptions = [
    { label: i18n.t('components.forms.deliveryForm.insured'), value: 'insured' },
    !hideContractorOption && { label: i18n.t('components.forms.deliveryForm.contractor'), value: 'contractor' },
    { label: i18n.t('components.forms.deliveryForm.third'), value: 'third' }
  ].filter((option) => option !== false) as { label: string; value: string }[];

  const modelToForm = (data: any) => {
    // Mapiranje na ulazu u formu
    const deliveryIV = Object.keys(data).reduce((acc: any, key: string) => {
      if (key !== "__typename") {
        const value = ((data[key].value === null || data[key].value === "" || data[key].value === 0) && storedValues && storedValues[key]) ? storedValues[key] : data[key].value;
        if (value !== null)
          acc[key] = value;
        else
          acc[key] = "";
      }
      return acc;
    }, {});

    deliveryIV.asEntity = storedValues?.asEntity || deliveryIV.asEntity || 'insured';
    return deliveryIV;
  }

  useEffect(() => {
    const modelData = modelToForm(delivery);
    const deliveryValues = {
      ...modelData,
      // ...storedValues
    };
    setDeliveryInitialValues(deliveryValues);
  }, [delivery]);

  const fields: FieldConfig[] = [
    {
      name: 'asEntity',
      component: RadioGroup,
      options: radioOptions,
      onChange: (value: any) => handleAsEntityChange(value),
    },
    {
      name: 'name',
      label: i18n.t('components.forms.deliveryForm.name'),
      component: InputText,
      disabled: !delivery.name.isEditable,
      disabledIf: (values: any) => values.asEntity !== 'third',
      validations: [
        { type: 'required', message: 'Potreban unos' },
      ] as Validation[],
    },
    {
      name: 'city',
      label: i18n.t('components.forms.deliveryForm.city'),
      component: InputCity,
      disabled: !(delivery.city.isEditable && delivery.cityKompareId.isEditable),
      disabledIf: (values: any) => values.asEntity !== 'third',
      formatValue: (values: any) => (values.city || values.zipCode) && `${values.city}, ${values.zipCode}`,
      validations: [
        { type: 'required', message: 'Potreban unos' },
      ] as Validation[],
    },
    {
      name: 'street',
      label: i18n.t('components.forms.deliveryForm.street'),
      component: InputStreet,
      componentProps: {
        cityKompareId: 'cityKompareId',
      },
      disabled: !(delivery.street.isEditable && delivery.streetKompareId.isEditable),
      disabledIf: (values: any) => values.asEntity !== 'third',
      onBlur: () => {
        validateForm(['street']);
      },
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validStreets', message: 'Adresa nije pronađena, pokušaj ponovno.', params: { streetsFound: 'streetsFound' } },
      ] as Validation[],
    },
    {
      name: 'houseNumber',
      label: i18n.t('components.forms.deliveryForm.number'),
      component: InputText,
      componentProps: {
        maxLength: 5,
        autoComplete: "new-houseNumber-lvl2"
      },
      disabled: !delivery.houseNumber.isEditable,
      disabledIf: (values: any) => values.asEntity !== 'third',
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validHouseNumber', message: 'Netočan unos. Unesi ispravan kućni broj.' },
      ] as Validation[],
      afterElement: delivery.city.isEditable && <FlexStyle gap={8} align='center'>
        <Button btnType='textPrimary' size='small' onClick={(e) => { e.preventDefault(); dispatch(openAddressModal({ type: 'delivery' })); }}>{i18n.t('components.forms.insuredPersonForm.cantFindAddress')}<b>{i18n.t('components.forms.insuredPersonForm.clickHere')}</b></Button>
      </FlexStyle>
    }
  ].filter((field) => asEntity === 'third' ? formItems.includes(field.name as DeliveryFormItemType) : field.name === 'asEntity');

  const formConfig: FormConfig = {
    gap: 24
  };

  if (!deliveryInitialValues) return null;

  return (
    <Form ref={formRef} fields={fields} config={formConfig} onSubmit={handleSubmit} initialValues={deliveryInitialValues} reduxName='delivery' />
  )
});
